import { toaster } from '@/components/ui/toaster';
import { DownloadAcknowledgmentDialog } from '@/components/mentions/DownloadAcknowledgmentDialog';
import { useDownloadAcknowledgmentStatusQuery } from '@/hooks/queries/use-download-acknowledgment-status-query';
import { useDownloadMentionMediaMutation } from '@/hooks/queries/use-download-mention-media-query';
import { useDownloadTikTokMention } from '@/hooks/actions/use-download-tiktok-mention';
import type { ButtonProps } from '@chakra-ui/react';
import { Button } from '@chakra-ui/react';
import { MentionPlatform } from '@fluenceur/types';
import axios from 'axios';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'iconoir-react';

interface DownloadMediaButtonProps extends ButtonProps {
  mentionId: string;
  isExpired?: boolean;
  disabled?: boolean;
  showText?: boolean;
  platform?: MentionPlatform;
}

/**
 * Button component for downloading mention media.
 *
 * For TikTok mentions the MP4 comes from the mp4-extractor Cloudflare Worker
 * (Akamai blocks direct server-side fetches); all other platforms use the
 * existing CDN-backed download endpoint.
 *
 * Gates the first download of a session behind an acknowledgment modal.
 * Subsequent clicks bypass it. Backend re-asserts on 412 in case the
 * Redis state was evicted; we re-open the modal in that case.
 */
export const DownloadMediaButton: React.FC<DownloadMediaButtonProps> = ({
  mentionId,
  isExpired = false,
  disabled = false,
  showText = true,
  platform,
  ...props
}) => {
  const { t } = useTranslation();
  const mediaMutation = useDownloadMentionMediaMutation();
  const tiktokMutation = useDownloadTikTokMention();
  const { data: ackStatus } = useDownloadAcknowledgmentStatusQuery();

  const [isAckDialogOpen, setIsAckDialogOpen] = useState(false);

  const isTikTok = platform === MentionPlatform.TIKTOK;
  const activeMutation = isTikTok ? tiktokMutation : mediaMutation;

  const triggerDownload = async () => {
    try {
      await activeMutation.mutateAsync(mentionId);
      toaster.success({
        title: t('mentions.download.started'),
        description: t('mentions.download.startedDescription'),
        duration: 3000,
      });
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 412) {
        // Backend disagreed about the acknowledgment state (TTL evicted,
        // logout in another tab, etc). Re-prompt the user.
        setIsAckDialogOpen(true);
        return;
      }
      console.error('Download failed:', error);
      toaster.error({
        title: t('mentions.download.failed'),
        description: t('mentions.download.failedDescription'),
        duration: 5000,
      });
    }
  };

  const handleClick = () => {
    if (ackStatus?.isAcknowledged) {
      void triggerDownload();
      return;
    }
    setIsAckDialogOpen(true);
  };

  const handleAcknowledged = () => {
    setIsAckDialogOpen(false);
    void triggerDownload();
  };

  const isDisabled = disabled || isExpired || activeMutation.isPending;

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={isDisabled}
        loading={activeMutation.isPending}
        {...props}
      >
        <Download width={showText ? 16 : 20} height={showText ? 16 : 20} />
        {showText && t('mentions.download.button')}
      </Button>
      <DownloadAcknowledgmentDialog
        isOpen={isAckDialogOpen}
        onClose={() => setIsAckDialogOpen(false)}
        onAcknowledged={handleAcknowledged}
      />
    </>
  );
};
